package org.teddapp.project.ui.finance

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Simulation
import com.tedd.db.Simulation_line
import org.teddapp.project.ui.LocalTeddFonts
import org.teddapp.project.ui.PaneRailItem
import org.teddapp.project.ui.PaneSpec
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddEmptyState
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddFilterChip
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddMenu
import org.teddapp.project.ui.TeddMenuEntry
import org.teddapp.project.ui.TeddPaneRail
import org.teddapp.project.ui.TeddSidePane
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.teddClickable

/**
 * L'onglet Simulation : un brouillon chiffré, nommé, isolé des comptes et des listes.
 * Une ligne est une étiquette et une expression ; sa valeur et le total sont calculés
 * par [SimulationMath] à chaque affichage, jamais stockés.
 *
 * Sur ordinateur, les brouillons vivent dans un panneau à gauche, le brouillon ouvert
 * à droite. Sur téléphone, une rangée de puces choisit le brouillon et les lignes sont
 * des cartes ; le total reste collé en bas.
 */
@Composable
internal fun SimulationTab(
    simulations: List<Simulation>,
    lines: List<Simulation_line>,
    selected: Simulation?,
    onSelect: (String) -> Unit,
    currency: String,
    compact: Boolean,
    onCreate: () -> Unit,
    onRename: (Simulation) -> Unit,
    onDuplicate: (Simulation) -> Unit,
    onDelete: (Simulation) -> Unit,
    onAddLine: (simulationId: String, label: String, expression: String) -> Unit,
    onEditLine: (line: Simulation_line, label: String, expression: String) -> Unit,
    onDeleteLine: (Simulation_line) -> Unit,
    onMoveLine: (line: Simulation_line, delta: Int) -> Unit,
) {
    val ownLines = remember(lines, selected?.id) { lines.filter { it.simulation_id == selected?.id }.sortedBy { it.order_index } }
    if (compact) {
        Column(modifier = Modifier.fillMaxSize()) {
            SimulationChips(simulations, selected?.id, onSelect, onCreate)
            if (selected == null) {
                SimulationEmpty(onCreate, modifier = Modifier.weight(1f))
            } else {
                SimulationSheet(
                    simulation = selected, lines = ownLines, currency = currency, compact = true,
                    onRename = { onRename(selected) }, onDuplicate = { onDuplicate(selected) }, onDelete = { onDelete(selected) },
                    onAddLine = { label, expr -> onAddLine(selected.id, label, expr) },
                    onEditLine = onEditLine, onDeleteLine = onDeleteLine, onMoveLine = onMoveLine,
                    modifier = Modifier.weight(1f),
                )
            }
        }
    } else {
        Row(modifier = Modifier.fillMaxSize()) {
            TeddSidePane(
                spec = SIMULATIONS_PANE,
                title = "les brouillons",
                icon = "draw",
                collapsedContent = { expand ->
                    TeddPaneRail(
                        expand = expand,
                        title = "les brouillons",
                        items = simulations.map { s ->
                            PaneRailItem(s.id, s.name, "draw", selected = s.id == selected?.id) { onSelect(s.id) }
                        },
                    )
                },
            ) {
                SimulationList(simulations, selected?.id, onSelect, onCreate)
            }
            Box(modifier = Modifier.weight(1f).fillMaxHeight()) {
                if (selected == null) {
                    SimulationEmpty(onCreate, modifier = Modifier.fillMaxSize())
                } else {
                    SimulationSheet(
                        simulation = selected, lines = ownLines, currency = currency, compact = false,
                        onRename = { onRename(selected) }, onDuplicate = { onDuplicate(selected) }, onDelete = { onDelete(selected) },
                        onAddLine = { label, expr -> onAddLine(selected.id, label, expr) },
                        onEditLine = onEditLine, onDeleteLine = onDeleteLine, onMoveLine = onMoveLine,
                        modifier = Modifier.fillMaxSize(),
                    )
                }
            }
        }
    }
}

/** Les brouillons : 240 dp par défaut, de 180 à 360. */
internal val SIMULATIONS_PANE = PaneSpec("finance.simulations", default = 240.dp, min = 180.dp, max = 360.dp, collapseBelow = 140.dp)

@Composable
private fun SimulationEmpty(onCreate: () -> Unit, modifier: Modifier = Modifier) {
    Box(modifier = modifier, contentAlignment = Alignment.Center) {
        TeddEmptyState(
            title = "Aucun brouillon",
            message = "Une simulation, c'est une liste d'étiquettes et de montants que l'on additionne : « 2 500 × 3 », « -15 000 », « 10% de 200 000 ».",
            icon = "draw",
            actionLabel = "Nouveau brouillon",
            onAction = onCreate,
        )
    }
}

// ── Choix du brouillon ─────────────────────────────────────────────────

@Composable
private fun SimulationList(simulations: List<Simulation>, selectedId: String?, onSelect: (String) -> Unit, onCreate: () -> Unit) {
    Column(modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()), verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(bottom = TeddSpace.S2),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text("Brouillons", color = TeddTokens.Ink, style = TeddType.BodyStrong, modifier = Modifier.weight(1f))
            TeddIconButton(onClick = onCreate) { TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted) }
        }
        simulations.forEach { s ->
            val on = s.id == selectedId
            val shape = RoundedCornerShape(TeddTokens.RSm)
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .teddClickable(onClick = { onSelect(s.id) }, shape = shape, fill = if (on) TeddTokens.Surface else Color.Transparent)
                    .then(if (on) Modifier.border(1.dp, TeddTokens.Line, shape) else Modifier)
                    .padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S2),
            ) {
                Text(s.name, color = TeddTokens.Ink, style = TeddType.Label.copy(fontWeight = FontWeight(if (on) 600 else 500)), maxLines = 1)
                val note = s.note?.takeIf { it.isNotBlank() }
                if (note != null) Text(note, color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 1)
            }
        }
    }
}

@Composable
private fun SimulationChips(simulations: List<Simulation>, selectedId: String?, onSelect: (String) -> Unit, onCreate: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()).padding(horizontal = 16.dp, vertical = 10.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        simulations.forEach { s ->
            TeddFilterChip(label = s.name, selected = s.id == selectedId, onClick = { onSelect(s.id) })
        }
        TeddFilterChip(label = "Nouveau", selected = false, icon = "plus", onClick = onCreate)
    }
}

// ── Le brouillon ouvert ────────────────────────────────────────────────

@Composable
private fun SimulationSheet(
    simulation: Simulation,
    lines: List<Simulation_line>,
    currency: String,
    compact: Boolean,
    onRename: () -> Unit,
    onDuplicate: () -> Unit,
    onDelete: () -> Unit,
    onAddLine: (label: String, expression: String) -> Unit,
    onEditLine: (line: Simulation_line, label: String, expression: String) -> Unit,
    onDeleteLine: (Simulation_line) -> Unit,
    onMoveLine: (line: Simulation_line, delta: Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    val side = if (compact) 16.dp else TeddSpace.S8
    var editingId by remember(simulation.id) { mutableStateOf<String?>(null) }
    val total = remember(lines) { SimulationMath.total(lines.map { it.expression }) }
    val unreadable = remember(lines) { lines.count { SimulationMath.evaluate(it.expression) == null } }

    Column(modifier = modifier) {
        // En-tête : nom, note, et le menu du brouillon.
        Row(
            modifier = Modifier.fillMaxWidth().padding(start = side, end = side, top = if (compact) 4.dp else TeddSpace.S6, bottom = TeddSpace.S3),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(simulation.name, color = TeddTokens.Ink, style = if (compact) TeddType.BodyStrong else TeddType.Heading, maxLines = 2)
                val note = simulation.note?.takeIf { it.isNotBlank() }
                if (note != null) Text(note, color = TeddTokens.InkMuted, style = TeddType.Caption)
            }
            SimulationMenu(onRename, onDuplicate, onDelete)
        }

        LazyColumn(
            modifier = Modifier.weight(1f).fillMaxWidth(),
            contentPadding = PaddingValues(start = side, end = side, bottom = TeddSpace.S4),
            verticalArrangement = Arrangement.spacedBy(if (compact) 8.dp else 0.dp),
        ) {
            items(lines, key = { it.id }) { line ->
                if (editingId == line.id) {
                    LineForm(
                        initialLabel = line.label, initialExpression = line.expression, currency = currency, compact = compact,
                        submitLabel = "Enregistrer",
                        onSubmit = { label, expr -> onEditLine(line, label, expr); editingId = null },
                        onCancel = { editingId = null },
                    )
                } else {
                    LineRow(
                        line = line, currency = currency, compact = compact,
                        onEdit = { editingId = line.id },
                        onDelete = { onDeleteLine(line) },
                        onUp = { onMoveLine(line, -1) },
                        onDown = { onMoveLine(line, +1) },
                    )
                }
            }
            item(key = "add") {
                Column(modifier = Modifier.padding(top = if (lines.isEmpty()) 0.dp else TeddSpace.S4)) {
                    Text("AJOUTER UNE LIGNE", color = TeddTokens.InkMuted, style = TeddType.Overline, modifier = Modifier.padding(bottom = TeddSpace.S2))
                    LineForm(
                        initialLabel = "", initialExpression = "", currency = currency, compact = compact,
                        submitLabel = "Ajouter", clearAfter = true,
                        onSubmit = { label, expr -> onAddLine(label, expr) },
                        onCancel = null,
                    )
                    Text(
                        text = "Une expression : « 2 500 × 3 », « -15 000 », « 10% de 200 000 », « (1 200 + 800) / 2 ».",
                        color = TeddTokens.InkSubtle, style = TeddType.Caption,
                        modifier = Modifier.padding(top = TeddSpace.S2),
                    )
                }
            }
        }

        TotalBar(total = total, count = lines.size, unreadable = unreadable, currency = currency, compact = compact)
    }
}

@Composable
private fun SimulationMenu(onRename: () -> Unit, onDuplicate: () -> Unit, onDelete: () -> Unit) {
    var open by remember { mutableStateOf(false) }
    Box {
        TeddIconButton(onClick = { open = true }) { TeddIcon(name = "more", size = 16.dp, color = TeddTokens.InkMuted) }
        TeddMenu(
            expanded = open,
            onDismiss = { open = false },
            alignEnd = true,
            width = 220.dp,
            entries = listOf(
                TeddMenuEntry("Renommer", icon = "edit", onClick = { open = false; onRename() }),
                TeddMenuEntry("Dupliquer", icon = "copy", onClick = { open = false; onDuplicate() }),
                TeddMenuEntry("Supprimer", icon = "trash", danger = true, onClick = { open = false; onDelete() }),
            ),
        )
    }
}

/** Une ligne : étiquette, expression telle que tapée, et sa valeur. Carte sur téléphone, rangée ailleurs. */
@Composable
private fun LineRow(
    line: Simulation_line,
    currency: String,
    compact: Boolean,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
    onUp: () -> Unit,
    onDown: () -> Unit,
) {
    val value = remember(line.expression) { SimulationMath.evaluate(line.expression) }
    var open by remember { mutableStateOf(false) }
    val shape = RoundedCornerShape(TeddTokens.RMd)
    val frame = if (compact) {
        Modifier.background(TeddTokens.Surface, shape).border(1.dp, TeddTokens.Line, shape).padding(horizontal = 14.dp, vertical = 12.dp)
    } else {
        Modifier.padding(vertical = 10.dp)
    }
    Column(modifier = Modifier.fillMaxWidth().teddClickable(onClick = onEdit, shape = shape).then(frame)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            Column(modifier = Modifier.weight(1f)) {
                Text(line.label.ifBlank { "Sans étiquette" }, color = TeddTokens.Ink, style = TeddType.Body, maxLines = 2)
                Text(line.expression, color = TeddTokens.InkMuted, style = TeddType.MonoSm, maxLines = 1)
            }
            LineValue(value, currency, large = false)
            Box {
                TeddIconButton(onClick = { open = true }) { TeddIcon(name = "more", size = 14.dp, color = TeddTokens.InkMuted) }
                TeddMenu(
                    expanded = open,
                    onDismiss = { open = false },
                    alignEnd = true,
                    width = 200.dp,
                    entries = listOf(
                        TeddMenuEntry("Modifier", icon = "edit", onClick = { open = false; onEdit() }),
                        TeddMenuEntry("Monter", icon = "arrow-up", onClick = { open = false; onUp() }),
                        TeddMenuEntry("Descendre", icon = "arrow-down", onClick = { open = false; onDown() }),
                        TeddMenuEntry("Supprimer", icon = "trash", danger = true, onClick = { open = false; onDelete() }),
                    ),
                )
            }
        }
    }
    if (!compact) Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
}

/** La valeur d'une ligne ou le total : en mono, rouge si négatif, « ? » si l'expression ne se lit pas. */
@Composable
private fun LineValue(value: Long?, currency: String, large: Boolean, modifier: Modifier = Modifier) {
    val color = when {
        value == null -> TeddTokens.InkSubtle
        value < 0L -> TeddTokens.Danger
        else -> TeddTokens.Ink
    }
    Text(
        text = if (value == null) "?" else fmtMoney(value, currency),
        color = color,
        style = if (large) TeddType.AmountLg else TeddType.Amount,
        textAlign = TextAlign.End,
        maxLines = 1,
        modifier = modifier,
    )
}

/**
 * Deux champs, étiquette puis expression, et la valeur qui se calcule pendant la frappe.
 * Entrée dans l'expression vaut le bouton. Sert à ajouter comme à modifier sur place.
 */
@Composable
private fun LineForm(
    initialLabel: String,
    initialExpression: String,
    currency: String,
    compact: Boolean,
    submitLabel: String,
    onSubmit: (label: String, expression: String) -> Unit,
    onCancel: (() -> Unit)?,
    clearAfter: Boolean = false,
) {
    var label by remember { mutableStateOf(initialLabel) }
    var expression by remember { mutableStateOf(initialExpression) }
    val value = remember(expression) { SimulationMath.evaluate(expression) }
    val valid = label.isNotBlank() && value != null
    val labelFocus = remember { FocusRequester() }
    val submit = {
        if (valid) {
            onSubmit(label.trim(), expression.trim())
            if (clearAfter) {
                label = ""
                expression = ""
                runCatching { labelFocus.requestFocus() }
            }
        }
    }
    val fields: @Composable (Modifier) -> Unit = { m ->
        SimField(value = label, placeholder = "Étiquette", onChange = { label = it }, modifier = m.focusRequester(labelFocus), imeAction = ImeAction.Next)
    }
    val exprField: @Composable (Modifier) -> Unit = { m ->
        SimField(value = expression, placeholder = "Expression", mono = true, onChange = { expression = it }, modifier = m, imeAction = ImeAction.Done, onDone = submit)
    }
    val actions: @Composable () -> Unit = {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            // Rien tant que l'expression est vide : le « ? » ne vaut que pour ce qui ne se lit pas.
            if (expression.isBlank()) Spacer(modifier = Modifier.width(96.dp))
            else LineValue(value, currency, large = false, modifier = Modifier.widthIn(min = 96.dp))
            if (onCancel != null) TeddButton(text = "Annuler", kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, onClick = onCancel)
            TeddButton(text = submitLabel, kind = TeddButtonKind.Primary, size = TeddButtonSize.Sm, enabled = valid, onClick = submit)
        }
    }
    val shape = RoundedCornerShape(TeddTokens.RMd)
    val frame = Modifier.fillMaxWidth().background(TeddTokens.SurfaceSunken, shape).padding(if (compact) 12.dp else TeddSpace.S3)
    if (compact) {
        Column(modifier = frame, verticalArrangement = Arrangement.spacedBy(8.dp)) {
            fields(Modifier.fillMaxWidth())
            exprField(Modifier.fillMaxWidth())
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) { actions() }
        }
    } else {
        Row(modifier = frame, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            fields(Modifier.weight(1.2f))
            exprField(Modifier.weight(1f))
            actions()
        }
    }
}

@Composable
private fun SimField(
    value: String,
    placeholder: String,
    onChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    mono: Boolean = false,
    imeAction: ImeAction = ImeAction.Default,
    onDone: (() -> Unit)? = null,
) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Box(
        modifier = modifier
            .height(40.dp)
            .clip(shape)
            .background(TeddTokens.Surface, shape)
            .border(1.dp, TeddTokens.Line, shape)
            .padding(horizontal = 12.dp),
        contentAlignment = Alignment.CenterStart,
    ) {
        val style = TextStyle(color = TeddTokens.Ink, fontSize = 14.sp, fontFamily = if (mono) LocalTeddFonts.current.mono else null)
        if (value.isEmpty()) Text(placeholder, style = style.copy(color = TeddTokens.InkSubtle), maxLines = 1)
        BasicTextField(
            value = value,
            onValueChange = onChange,
            singleLine = true,
            textStyle = style,
            cursorBrush = SolidColor(TeddTokens.LimeStrong),
            modifier = Modifier.fillMaxWidth(),
            keyboardOptions = KeyboardOptions(imeAction = imeAction),
            keyboardActions = KeyboardActions(onDone = { onDone?.invoke() }),
        )
    }
}

/** Le total, gros, en bas ; sur téléphone il laisse la place du bouton flottant à droite. */
@Composable
private fun TotalBar(total: Long, count: Int, unreadable: Int, currency: String, compact: Boolean) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(TeddTokens.Surface)
                .padding(start = if (compact) 16.dp else TeddSpace.S8, end = if (compact) 92.dp else TeddSpace.S8, top = TeddSpace.S3, bottom = TeddSpace.S3),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text("TOTAL", color = TeddTokens.InkMuted, style = TeddType.Overline)
                val detail = buildString {
                    append(if (count <= 1) "$count ligne" else "$count lignes")
                    if (unreadable > 0) append(" · ").append(if (unreadable == 1) "1 illisible ne compte pas" else "$unreadable illisibles ne comptent pas")
                }
                Text(detail, color = TeddTokens.InkSubtle, style = TeddType.Caption)
            }
            LineValue(total, currency, large = true)
        }
    }
}

// ── Nom et note d'un brouillon ─────────────────────────────────────────

/** Crée ou renomme un brouillon : un nom, une note facultative. */
@Composable
internal fun SimulationNameDialog(
    existing: Simulation?,
    onClose: () -> Unit,
    onSave: (name: String, note: String?) -> Unit,
) {
    var name by remember(existing?.id) { mutableStateOf(existing?.name ?: "") }
    var note by remember(existing?.id) { mutableStateOf(existing?.note ?: "") }
    val valid = name.isNotBlank()
    val save = { if (valid) onSave(name.trim(), note.trim().ifBlank { null }) }
    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Box(modifier = Modifier.fillMaxSize().padding(16.dp), contentAlignment = Alignment.Center) {
            Column(
                modifier = Modifier
                    .widthIn(max = 440.dp)
                    .fillMaxWidth()
                    .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RLg))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RLg))
                    .padding(TeddSpace.S5),
                verticalArrangement = Arrangement.spacedBy(TeddSpace.S4),
            ) {
                Text(
                    text = if (existing == null) "NOUVEAU BROUILLON" else "RENOMMER LE BROUILLON",
                    color = TeddTokens.InkMuted, style = TeddType.Overline,
                )
                TeddField(label = "Nom", value = name, onValueChange = { name = it }, placeholder = "Voiture d'occasion")
                TeddField(label = "Note (facultative)", value = note, onValueChange = { note = it }, placeholder = "Ce que ce brouillon simule")
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    Spacer(modifier = Modifier.weight(1f))
                    TeddButton(text = "Annuler", kind = TeddButtonKind.Ghost, onClick = onClose)
                    TeddButton(text = if (existing == null) "Créer" else "Enregistrer", kind = TeddButtonKind.Primary, enabled = valid, onClick = save)
                }
            }
        }
    }
}
